import {Box, Button, Checkbox, Flex, HStack, Spacer, Text} from '@chakra-ui/react';
import {useServerConfig} from 'api/queries';
import {TitledSection} from 'components/layouts/aside-layout/components';
import {colors} from 'components/providers';
import {useCardPayment} from 'components/providers/CardPaymentProvider';
import {CheckboxListBlock} from 'components/shared';
import {useTranslation} from 'hooks';
import {FC} from 'react';
import {PaymentMethodEnum} from 'types';
import ImageUI from 'ui/ImageUI';
import {SpacerUI} from 'ui/SpacerUI';
import {validateCardType} from 'utils/cartUtils';

interface Props {
  method: PaymentMethodEnum;
  onChange: (method: PaymentMethodEnum) => void;
  isOnlinePaymentAvailable: boolean | undefined;
  saveCard: boolean;
  onChangeSaveCard: (saveCard: boolean) => void;
}

export const PaymentMethod: FC<Props> = ({isOnlinePaymentAvailable, method, saveCard, onChange, onChangeSaveCard}) => {
  const {onOpenCardsModal, selectedCard, cardQuery} = useCardPayment();
  const {t} = useTranslation();
  const {data: serverConfig} = useServerConfig();

  const cardType = validateCardType(selectedCard?.masked_card_number);
  const icon =
    selectedCard && selectedCard.id == 0
      ? '/icons/addNewCard.svg'
      : selectedCard && cardType === 'VISA'
      ? '/icons/myCardsVisa.svg'
      : '/icons/myCardsMasterCard.svg';

  const isMaibDisabled = serverConfig?.maib_disabled ?? false;
  const isBpayDisabled = serverConfig?.bpay_disabled ?? false;

  const items = [
    {
      value: PaymentMethodEnum.CASH,
      label: t('CASH_PAYMENT_ON'),
    },
    {
      value: PaymentMethodEnum.MIA,
      label: (
        <Flex flexDirection={['column', 'row']} alignItems={['start', 'center']}>
          <Text whiteSpace="nowrap" fontSize="16px" fontWeight={600} as="span">
            {t('MIA_PAYMENT')}
          </Text>
          <Flex>
            <ImageUI src="/icons/mia.svg" ml={['0px', '10px']} width="30px" border="1px solid black" borderRadius={2} />
          </Flex>
          {isBpayDisabled && (
            <Text fontSize="12px" color="red.500" ml={['0px', '10px']} mt={['5px', '0px']}>
              ({t('paymentMethodTemporarilyDisabled')})
            </Text>
          )}
        </Flex>
      ),
      disabled: !isOnlinePaymentAvailable || isBpayDisabled,
    },
    {
      value: PaymentMethodEnum.CARD,
      label: (
        <Box>
          <Flex flexDirection={['column', 'row']} mb="6px" justifyItems={['center', 'flex-start']}>
            <Text whiteSpace="nowrap" fontSize="16px" fontWeight={600} as="span">
              {t('ONLINE_CARD_PAYMENT')}
            </Text>
            <Flex>
              <ImageUI src="/icons/masterCardIcon.svg" ml={['0px', '10px']} />
              <ImageUI src="/icons/visaCardIcon.svg" alt="Visa Card" ml="10px" />
            </Flex>
            {isMaibDisabled && (
              <Text fontSize="12px" color="red.500" ml={['0px', '40px']} mt={['5px', '0px']}>
                ({t('paymentMethodTemporarilyDisabled')})
              </Text>
            )}
          </Flex>
          <Text as="div" fontSize="14px" fontWeight={500} color={colors.textGray}>
            {t('ONLINE_CARD_PAYMENT_MESSAGE')}
          </Text>

          {method == 'card' && !isMaibDisabled ? (
            <Box flex={1} textAlign="center" mt="10px">
              {selectedCard && (
                <HStack>
                  <ImageUI src={icon} />
                  <Text>{selectedCard.masked_card_number}</Text>
                  <Spacer />
                  {!!cardQuery?.data?.length && (
                    <Button
                      variant="outline"
                      onClick={onOpenCardsModal}
                      leftIcon={<ImageUI src="/edit-icon.svg" alt="icon" />}
                    >
                      {t('changeCard')}
                    </Button>
                  )}
                </HStack>
              )}
              <Box my={3}>
                <hr />
              </Box>
              {selectedCard?.id === 0 || !selectedCard ? (
                <Flex my="15px">
                  <Checkbox
                    isChecked={saveCard}
                    variant="circle"
                    alignItems="flex-start"
                    onChange={() => onChangeSaveCard(true)}
                  >
                    <Text>{t('saveCard')}</Text>
                  </Checkbox>
                  <SpacerUI mode="horizontal" size={50} />
                  <Checkbox
                    isChecked={!saveCard}
                    variant="circle"
                    alignItems="flex-start"
                    onChange={() => onChangeSaveCard(false)}
                  >
                    <Text>{t('dontSaveCard')}</Text>
                  </Checkbox>
                </Flex>
              ) : null}
            </Box>
          ) : null}
        </Box>
      ),
      disabled: !isOnlinePaymentAvailable || isMaibDisabled,
    },
  ];

  const basePaymentMethods = [
    ...items,
    {
      value: PaymentMethodEnum.ApplePayGPay,
      label: (
        <Flex flexDirection={['column', 'row']} alignItems={['start', 'center']}>
          <Text whiteSpace="nowrap" fontSize="16px" fontWeight={600} as="span">
            {t('payApplePayGpay')}
          </Text>
          <Flex>
            <ImageUI src="/icons/applePayIcon.svg" ml={['0px', '10px']} />
            <ImageUI src="/icons/gPayIcon.svg" alt="Visa Card" ml="10px" />
          </Flex>
          {isMaibDisabled && (
            <Text fontSize="12px" color="red.500" ml={['0px', '10px']} mt={['5px', '0px']}>
              ({t('paymentMethodTemporarilyDisabled')})
            </Text>
          )}
        </Flex>
      ),
      disabled: !isOnlinePaymentAvailable || isMaibDisabled,
    },
  ];

  return (
    <TitledSection title={t('PAYMENT_METHOD')}>
      <CheckboxListBlock items={basePaymentMethods} value={method} onChange={onChange} />
    </TitledSection>
  );
};
